import { Box, Stack } from "@mui/material";
import { Notification } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { GOOGLE_API_KEY } from "config";
import i18next from "i18next";
import { useCallback, useEffect } from "react";
import { usePlacesWidget } from "react-google-autocomplete";
import { useTranslation } from "react-i18next";
import { useCentralStore } from "stores";
import StructureAddressSection from "../components/StructureAddressSection";
import StructureDescriptionSection from "../components/StructureDescriptionSection";
import StructureFormFooter from "../components/StructureFormFooter";
import StructureProfileSection from "../components/StructureProfileSection";
import { useAddressQueries } from "../hooks/useAddressQueries";
import { useCreateStructureForm } from "../hooks/useCreateStructureForm";

export function CreateStructure() {
  // Store hooks
  const setHeaderTitle = useCentralStore(
    useCallback(state => state.setHeaderTitle, [])
  );
  const setHeaderBackground = useCentralStore(
    useCallback(state => state.setHeaderBackground, [])
  );

  const { t } = useTranslation();

  // Custom hooks
  const {
    register,
    unregister,
    watch,
    setValue,
    control,
    handleSubmit,
    errors,
    parentStructureOptions,
    structureTypeOptions,
    isLoadingDenominations,
    isLoadingStructureTypes,
    denominationMutation,
    onSubmit
  } = useCreateStructureForm();

  const addressQueries = useAddressQueries(watch);

  // Google Places widget
  const { ref: placesRef } = usePlacesWidget({
    apiKey: GOOGLE_API_KEY,
    onPlaceSelected: useCallback(
      place => {
        setValue("latitude", place.geometry.location.lat());
        setValue("longitude", place.geometry.location.lng());
      },
      [setValue]
    ),
    options: {
      types: ["establishment"],
      componentRestrictions: { country: "et" }
    }
  });

  // Effects
  useEffect(() => {
    setHeaderTitle(t("denominations.structures.createStructure"));
    return () => setHeaderTitle("");
  }, [setHeaderTitle, i18next.language]);

  useEffect(() => {
    setHeaderBackground(true);
    return () => setHeaderBackground(true);
  }, [setHeaderBackground]);

  return (
    <Box p={{ xs: 4, lg: 6 }}>
      <Notification />
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={6}>
        <StructureProfileSection
          register={register}
          unregister={unregister}
          watch={watch}
          setValue={setValue}
          control={control}
          errors={errors}
          mutationError={denominationMutation.error}
          structureTypeOptions={structureTypeOptions}
          parentStructureOptions={parentStructureOptions}
          isLoadingStructureTypes={isLoadingStructureTypes}
          isLoadingDenominations={isLoadingDenominations}
        />

        <StructureAddressSection
          register={register}
          setValue={setValue}
          watch={watch}
          errors={errors}
          {...addressQueries}
          placesRef={placesRef}
        />

        <StructureDescriptionSection
          control={control}
          errors={errors}
          mutationError={denominationMutation.error}
        />

        <MutationError error={denominationMutation.error} />

        <StructureFormFooter
          control={control}
          errors={errors}
          isLoading={denominationMutation.isLoading}
          onSubmit={handleSubmit(onSubmit)}
        />
      </Stack>
    </Box>
  );
}
